/**
 * Analytics tracking utilities for GA4 and Meta Pixel.
 * 
 * GA4 Measurement ID and Meta Pixel ID are injected via index.html script tags.
 * This module provides typed helpers for custom event tracking.
 */

// ── GA4 helpers ──────────────────────────────────────────────

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

function gtag(...args: unknown[]) {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag(...args);
  }
}

// ── Meta Pixel helpers ───────────────────────────────────────

function fbq(...args: unknown[]) {
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq(...args);
  }
}

// ── UTM persistence ──────────────────────────────────────────

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

/** Capture UTM params from the URL on page load and store in sessionStorage */
export function captureUtmParams(): UtmParams {
  const params = new URLSearchParams(window.location.search);
  const utmKeys: (keyof UtmParams)[] = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ];

  const utm: UtmParams = {};
  let hasUtm = false;

  utmKeys.forEach((key) => {
    const val = params.get(key);
    if (val) {
      utm[key] = val;
      hasUtm = true;
    }
  });

  if (hasUtm) {
    sessionStorage.setItem("utm_params", JSON.stringify(utm));
  }

  return hasUtm ? utm : getStoredUtmParams();
}

export function getStoredUtmParams(): UtmParams {
  try {
    const raw = sessionStorage.getItem("utm_params");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

// ── Tracked events ───────────────────────────────────────────

/** Default ticket price (USD) used when an event has no explicit price set */
export const DEFAULT_TICKET_PRICE = 15;

/**
 * Meta Pixel standard InitiateCheckout — fires when a user clicks a
 * "Get Tickets" CTA. Reports value + currency so Meta ad sets can
 * optimize for conversion intent (Eventbrite handles actual purchase).
 * Mirrored to GA4 as `begin_checkout`.
 */
export function trackInitiateCheckout(params: {
  eventName: string;
  eventId: string;
  ticketUrl: string;
  price?: number | null;
}) {
  const utm = getStoredUtmParams();
  const value = params.price && params.price > 0 ? params.price : DEFAULT_TICKET_PRICE;

  fbq("track", "InitiateCheckout", {
    value,
    currency: "USD",
    content_name: params.eventName,
    content_ids: [params.eventId],
    content_type: "product",
    num_items: 1,
    ...utm,
  });

  gtag("event", "begin_checkout", {
    currency: "USD",
    value,
    items: [
      {
        item_id: params.eventId,
        item_name: params.eventName,
        price: value,
        quantity: 1,
      },
    ],
    ticket_url: params.ticketUrl,
    ...utm,
  });
}

/** Outbound click to Eventbrite ticket page */
export function trackTicketClick(eventName: string, ticketUrl: string) {
  const utm = getStoredUtmParams();

  gtag("event", "ticket_click", {
    event_category: "outbound",
    event_label: eventName,
    ticket_url: ticketUrl,
    ...utm,
  });

  fbq("trackCustom", "TicketClick", {
    event_name: eventName,
    ticket_url: ticketUrl,
    ...utm,
  });
}

/** Vendor application page visit / form start */
export function trackVendorApplyClick(source: string) {
  const utm = getStoredUtmParams();

  gtag("event", "vendor_apply_click", {
    event_category: "engagement",
    event_label: source,
    ...utm,
  });

  fbq("trackCustom", "VendorApplyClick", {
    source,
    ...utm,
  });
}

/** Vendor application submitted */
export function trackVendorApplicationSubmit(eventId: string, companyName: string) {
  const utm = getStoredUtmParams();

  gtag("event", "vendor_application_submit", {
    event_category: "conversion",
    event_id: eventId,
    company_name: companyName,
    ...utm,
  });

  fbq("trackCustom", "VendorApplicationSubmit", {
    event_id: eventId,
    company_name: companyName,
    ...utm,
  });
}

/** CTA button clicks (e.g. "Apply Now" on homepage) */
export function trackCtaClick(ctaName: string, destination: string) {
  const utm = getStoredUtmParams();

  gtag("event", "cta_click", {
    event_category: "engagement",
    event_label: ctaName,
    destination,
    ...utm,
  });

  fbq("trackCustom", "CTAClick", {
    cta_name: ctaName,
    destination,
    ...utm,
  });
}

/** Email newsletter signup */
export function trackEmailSignup() {
  const utm = getStoredUtmParams();

  gtag("event", "email_signup", {
    event_category: "conversion",
    ...utm,
  });

  fbq("track", "Lead", {
    content_name: "email_signup",
    ...utm,
  });
}

/** Standard ViewContent event for content/detail pages */
export function trackViewContent(title: string, id: string) {
  const utm = getStoredUtmParams();

  fbq("track", "ViewContent", {
    content_name: title,
    content_ids: [id],
    content_type: "product",
    ...utm,
  });

  gtag("event", "view_content", {
    event_category: "engagement",
    event_label: title,
    content_id: id,
    ...utm,
  });
}

/** Activity spotlight badge impression */
export function trackActivitySpotlightImpression(activityId: string) {
  const utm = getStoredUtmParams();

  gtag("event", "activity_spotlight_impression", {
    event_category: "engagement",
    activity_id: activityId,
    ...utm,
  });

  fbq("trackCustom", "ActivitySpotlightImpression", {
    activity_id: activityId,
    ...utm,
  });
}

/** Activity spotlight click */
export function trackActivitySpotlightClick(activityId: string, cta: string) {
  const utm = getStoredUtmParams();

  gtag("event", "activity_spotlight_click", {
    event_category: "engagement",
    activity_id: activityId,
    cta,
    ...utm,
  });

  fbq("trackCustom", "ActivitySpotlightClick", {
    activity_id: activityId,
    cta,
    ...utm,
  });
}

/** Page view with UTM context (called in addition to GA4 auto-pageview) */
export function trackPageView(pagePath: string) {
  const utm = getStoredUtmParams();

  gtag("event", "page_view_custom", {
    page_path: pagePath,
    ...utm,
  });

  // Meta Pixel tracks PageView automatically via init
}
